import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from "react";
import { LineChart } from "@libs/enigma";
import { Badge, Card } from "@libs/rubick";
import { getTimeWindowMinutes, SHARED_TIME_RANGE_OPTIONS } from "@/constants/timeRange";
const CHART_COLORS = [
    "#38BDF8",
    "#FB923C",
    "#4ADE80",
    "#FACC15",
    "#F87171",
    "#A78BFA",
    "#2DD4BF",
    "#F472B6",
    "#818CF8",
    "#34D399"
];
function getTimeConfig(range) {
    return {
        timeWindowMinutes: getTimeWindowMinutes(range)
    };
}
function TimeRangeSelector({ selected, onChange }) {
    return /*#__PURE__*/ _jsx("div", {
        className: "inline-flex rounded-lg bg-gray-100 p-1",
        children: SHARED_TIME_RANGE_OPTIONS.map((range)=>/*#__PURE__*/ _jsx("button", {
                type: "button",
                onClick: ()=>onChange(range.value),
                className: `rounded-md px-3 py-1.5 text-sm transition ${selected === range.value ? "bg-navy-70 text-white font-medium" : "text-gray-600 hover:bg-gray-200"}`,
                children: range.label
            }, range.value))
    });
}
function ChartSkeleton() {
    return /*#__PURE__*/ _jsx("div", {
        className: "flex items-end justify-between h-80 gap-0.5 px-12 pb-8",
        children: Array.from({
            length: 24
        }).map((_, i)=>/*#__PURE__*/ _jsx("div", {
                className: "flex-1 bg-gray-200 rounded-t animate-pulse",
                style: {
                    height: `${20 + Math.random() * 60}%`
                }
            }, i))
    });
}
function ChartLegend({ datasets }) {
    return /*#__PURE__*/ _jsx("div", {
        className: "flex flex-wrap gap-x-5 gap-y-2 px-4 text-xs text-gray-500",
        children: datasets.map((dataset)=>/*#__PURE__*/ _jsxs("div", {
                className: "flex min-w-0 items-center gap-2 max-w-full",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "h-2.5 w-2.5 shrink-0 rounded-sm opacity-40",
                        style: {
                            backgroundColor: dataset.color ?? "#38BDF8"
                        }
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "break-all",
                        children: dataset.label ?? "Value"
                    })
                ]
            }, dataset.label))
    });
}
export function ConsumptionChart({ data, isLoading, timeRange, onTimeRangeChange }) {
    const { timeWindowMinutes } = getTimeConfig(timeRange);
    const { totalKeyCount } = data;
    // Filter to the selected time window, rank by total, take top 10
    const { datasets, yMax, displayCount } = useMemo(()=>{
        const cutoff = Date.now() - timeWindowMinutes * 60 * 1000;
        // Filter points per key, compute windowed total for ranking
        const withTotals = data.keys.map((key)=>{
            const filtered = key.points.filter((p)=>p.x >= cutoff);
            const total = filtered.reduce((s, p)=>s + p.y, 0);
            return {
                ...key,
                filteredPoints: filtered,
                total
            };
        }).filter((k)=>k.total > 0).sort((a, b)=>b.total - a.total);
        const top10 = withTotals.slice(0, 10);
        const datasets = top10.map((key, i)=>({
                data: key.filteredPoints.map((p)=>({
                        x: p.x,
                        y: p.y / 1024
                    })),
                color: CHART_COLORS[i % CHART_COLORS.length],
                label: key.apiKeyName
            }));
        let maxY = 0;
        for (const ds of datasets){
            for (const pt of ds.data){
                if (pt.y > maxY) maxY = pt.y;
            }
        }
        const yMax = Math.max(Math.ceil(maxY * 1.15), 1);
        return {
            datasets,
            yMax,
            displayCount: Math.min(totalKeyCount, 10)
        };
    }, [
        data.keys,
        timeWindowMinutes,
        totalKeyCount
    ]);
    const now = new Date();
    const beg = new Date(now.getTime() - timeWindowMinutes * 60 * 1000);
    return /*#__PURE__*/ _jsx(Card, {
        variant: "bordered",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "flex flex-col gap-4",
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center justify-between flex-wrap gap-2",
                    children: [
                        /*#__PURE__*/ _jsx("h3", {
                            className: "text-sm font-semibold uppercase tracking-wider text-gray-500",
                            children: "Entropy Consumption by Key"
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center gap-3",
                            children: [
                                totalKeyCount > 0 && /*#__PURE__*/ _jsxs(Badge, {
                                    color: "zinc",
                                    children: [
                                        "Top ",
                                        displayCount,
                                        " of ",
                                        totalKeyCount.toLocaleString(),
                                        " keys"
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(TimeRangeSelector, {
                                    selected: timeRange,
                                    onChange: onTimeRangeChange
                                })
                            ]
                        })
                    ]
                }),
                isLoading ? /*#__PURE__*/ _jsx(ChartSkeleton, {}) : datasets.length === 0 || totalKeyCount === 0 ? /*#__PURE__*/ _jsx("div", {
                    className: "flex items-center justify-center h-80",
                    children: /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-gray-500",
                        children: "No consumption data available for the selected period"
                    })
                }) : /*#__PURE__*/ _jsxs("div", {
                    className: "flex flex-col gap-3",
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: "h-72 [&_.legend]:hidden",
                            children: /*#__PURE__*/ _jsx(LineChart, {
                                datasets: datasets,
                                timeWindow: timeWindowMinutes,
                                yMax: yMax,
                                yUnits: " KB",
                                yTicks: 4,
                                xTicks: timeRange === "30d" ? 10 : timeRange === "7d" ? 7 : 6,
                                beg: beg,
                                end: now,
                                padding: {
                                    x: 15,
                                    y: 0
                                },
                                containerClass: "h-full w-full"
                            })
                        }),
                        /*#__PURE__*/ _jsx(ChartLegend, {
                            datasets: datasets
                        })
                    ]
                })
            ]
        })
    });
}
